import { useEffect, useState } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';

const CLIENT_ID = process.env.REACT_APP_MICROSOFT_CLIENT_ID || '';
const TENANT_ID = process.env.REACT_APP_MICROSOFT_TENANT_ID || '';
const LOGIN_SCOPES = ['openid', 'profile', 'email'];

let msalApp = null;
let msalReady = null;

/**
 * Builds the tenant-scoped MSAL client once for the login popup.
 */
function getMsalApp() {
  if (!msalReady) {
    msalApp = new PublicClientApplication({
      auth: {
        clientId: CLIENT_ID,
        authority: `https://login.microsoftonline.com/${TENANT_ID}`,
        redirectUri: window.location.origin
      },
      cache: {
        cacheLocation: 'sessionStorage'
      }
    });
    msalReady = msalApp.initialize();
  }
  return msalReady;
}

/**
 * Renders a Microsoft sign-in button when the Entra client and tenant are set.
 * The popup must open in the click itself; awaiting first makes the browser block it.
 */
function MicrosoftSignInButton({ onCredential, onError, disabled }) {
  const [pending, setPending] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!CLIENT_ID || !TENANT_ID) {
      return undefined;
    }
    let cancelled = false;
    getMsalApp()
      .then(() => {
        if (!cancelled) {
          setReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          onError('Não foi possível preparar o login da Microsoft.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [onError]);

  if (!CLIENT_ID || !TENANT_ID) {
    return null;
  }

  /**
   * Opens the Entra popup and returns the ID token to the login screen.
   */
  const handleClick = () => {
    if (!msalApp || !ready) {
      onError('O login da Microsoft ainda está carregando. Tente de novo.');
      return;
    }

    setPending(true);
    msalApp.loginPopup({ scopes: LOGIN_SCOPES, prompt: 'select_account' })
      .then((result) => {
        if (!result?.idToken) {
          onError('A Microsoft não devolveu um token de acesso.');
          return;
        }
        onCredential(result.idToken);
      })
      .catch((error) => {
        if (error?.errorCode === 'user_cancelled') {
          return;
        }
        const detail = error?.errorMessage || error?.message;
        onError(detail || 'Falha ao abrir o login da Microsoft.');
      })
      .finally(() => {
        setPending(false);
      });
  };

  return (
    <button
      type="button"
      className="microsoft-signin-button"
      onClick={handleClick}
      disabled={disabled || pending || !ready}
    >
      <span className="microsoft-mark" aria-hidden>
        <i /><i /><i /><i />
      </span>
      Continuar com Microsoft
    </button>
  );
}

export default MicrosoftSignInButton;
